import { useCallback, useReducer, useRef } from 'react'

import { addMonths } from 'date-fns'

import {
  DriverLocationTrackingReportInputDtoInput,
  LiveTrackingDataPoint,
  useGetDriverLocationTrackingReportLazyQuery,
} from 'generated/graphql'

const CHUNK_SIZE_MONTHS = 1

function buildDateChunks(start: Date, end: Date): Array<{ start: Date; end: Date }> {
  const chunks: Array<{ start: Date; end: Date }> = []
  let current = start
  while (current < end) {
    const next = addMonths(current, CHUNK_SIZE_MONTHS)
    chunks.push({
      start: current,
      end: next > end ? end : next,
    })
    current = next
  }
  return chunks
}

type Progress = { completed: number; total: number }

type State = {
  loading: boolean
  dataPoints: LiveTrackingDataPoint[]
  progress: Progress
}

type Action =
  | { type: 'FETCH_START'; totalChunks: number }
  | { type: 'CHUNK_LOADED'; dataPoints: LiveTrackingDataPoint[] }
  | { type: 'QUERY_FAILED' }

const initialState: State = {
  loading: false,
  dataPoints: [],
  progress: { completed: 0, total: 0 },
}

function advanceProgress(state: State, updates: Partial<State>): State {
  const completed = state.progress.completed + 1
  const done = completed === state.progress.total
  return {
    ...state,
    ...updates,
    loading: !done,
    progress: { ...state.progress, completed },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_START':
      return {
        ...state,
        loading: true,
        dataPoints: [],
        progress: { completed: 0, total: action.totalChunks },
      }
    case 'CHUNK_LOADED':
      return advanceProgress(state, {
        dataPoints: [...state.dataPoints, ...action.dataPoints],
      })
    case 'QUERY_FAILED':
      return advanceProgress(state, {})
  }
}

export const useDriverLocationTracking = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const generationRef = useRef(0)

  const [fetchReport] = useGetDriverLocationTrackingReportLazyQuery({
    fetchPolicy: 'network-only',
  })

  const fetchData = useCallback(
    async (filter: DriverLocationTrackingReportInputDtoInput) => {
      const generation = ++generationRef.current

      const start = new Date(filter.start!)
      const end = new Date(filter.end!)
      const chunks = buildDateChunks(start, end)

      dispatch({ type: 'FETCH_START', totalChunks: chunks.length })

      for (const chunk of chunks) {
        if (generationRef.current !== generation) break
        try {
          const r = await fetchReport({
            variables: {
              input: {
                start: chunk.start,
                end: chunk.end,
                userType: filter.userType,
              },
            },
          })
          if (generationRef.current !== generation) break
          dispatch({
            type: 'CHUNK_LOADED',
            dataPoints: r.data?.driverLocationTrackingReport.dataPoints ?? [],
          })
        } catch {
          if (generationRef.current !== generation) break
          dispatch({ type: 'QUERY_FAILED' })
        }
      }
    },
    [fetchReport],
  )

  return {
    loading: state.loading,
    progress: state.progress,
    dataPoints: state.dataPoints,
    fetchData,
  }
}
